import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useTournaments } from '../../../hooks/useTournaments';
import Sidebar from '../../admin-dashboard/components/Sidebar';

/**
 * Admin V2 Tournaments — Liste + création de tournois.
 * Route : /admin/tournaments + /admin/v2/tournaments. Feature : pages.tournaments.admin.
 */
const STATUS_CLS = {
  draft: 'bg-muted text-muted-foreground',
  recruiting: 'bg-primary/10 text-primary',
  active: 'bg-success/10 text-success',
  completed: 'bg-accent/10 text-accent',
};

const AdminV2Tournaments = ({ basePath = '/admin/tournaments', readOnly = false }) => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName, servers } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', format: '1v1', server: '', starts_at: '', ends_at: '' });
  const [saving, setSaving] = useState(false);
  const { rows, loading, error, reload, create } = useTournaments();

  const serverOptions = (servers && servers.length > 0) ? servers : ['MENA', 'FR+', 'IT+', 'DE+', 'US+'];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await create({ ...form, server: form.server || serverOptions[0] });
      setShowCreate(false);
      setForm({ title: '', format: '1v1', server: '', starts_at: '', ends_at: '' });
    } catch (err) {
      alert(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">{t('tournamentsV2.title', 'Tournaments')}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">{tenantName} · {rows.length}</p>
            </div>
            {!readOnly && (
              <button type="button" onClick={() => setShowCreate((s) => !s)}
                className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
                {t('tournamentsV2.create', 'New tournament')}
              </button>
            )}
          </div>
        </header>
        <div className="p-4 md:p-6 space-y-4">
          {showCreate && !readOnly && (
            <form onSubmit={submit} className="rounded-2xl bg-card border border-border p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder={t('tournamentsV2.formTitle', 'Title')} required
                className="md:col-span-2 px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary" />
              <select value={form.format} onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))}
                className="px-3 py-2 rounded-lg bg-background border border-border text-base">
                <option value="1v1">1v1</option><option value="2v2">2v2</option><option value="bracket">Bracket</option>
              </select>
              <select value={form.server} onChange={(e) => setForm((f) => ({ ...f, server: e.target.value }))}
                className="px-3 py-2 rounded-lg bg-background border border-border text-base">
                {serverOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="date" value={form.starts_at} onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                className="px-3 py-2 rounded-lg bg-background border border-border text-base" />
              <input type="date" value={form.ends_at} onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                className="px-3 py-2 rounded-lg bg-background border border-border text-base" />
              <div className="md:col-span-2 flex gap-2">
                <button type="submit" disabled={saving} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60">
                  {saving ? t('tournamentsV2.creating', 'Creating…') : t('tournamentsV2.create', 'Create')}
                </button>
                <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-2 rounded-lg border border-border text-sm">{t('common.cancel', 'Cancel')}</button>
              </div>
            </form>
          )}

          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">{t('common.retry', 'Retry')}</button>
            </div>
          )}

          {loading ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('tournamentsV2.empty', 'No tournaments yet.')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {rows.map((tr) => (
                <Link key={tr.id} to={`${basePath}/${tr.id}`}
                  className="rounded-2xl bg-card border border-border p-5 hover:border-primary/40 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-heading font-semibold text-foreground truncate">{tr.title}</div>
                    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${STATUS_CLS[tr.status] || 'bg-muted text-muted-foreground'}`}>
                      {t(`tournamentsV2.status.${tr.status}`, tr.status)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    {tr.format} · {tr.server || '—'}
                  </div>
                  {(tr.starts_at || tr.ends_at) && (
                    <div className="text-[10px] text-muted-foreground/70 mt-1">
                      {tr.starts_at ? new Date(tr.starts_at).toLocaleDateString() : '?'} → {tr.ends_at ? new Date(tr.ends_at).toLocaleDateString() : '?'}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2Tournaments;
